import { useEditorState } from "@/store";
import { openDB, deleteDB } from "idb"
import { debounce } from "lodash";
import { message } from "antd";

import i18n from "@/i18n";
import axios from "axios";
import http from "isomorphic-git/http/web";
import LFS from "@isomorphic-git/lightning-fs";
import * as git from "isomorphic-git";
import * as Sentry from "@sentry/react";

import type { DBSchema, IDBPDatabase } from "idb";
import {
	FileSystemError,
	clearMonacoTabName,
	updateMonacoTabName,
	clearBlocklyTabName,
	updateBlocklyTabName,
	isPublicProject
} from "@/utils";

interface FileRecord {
	content: string,
	metadata: {
		version: number,
		timestamp: number,
	}
}

interface CacheItem {
	fileRecordList: FileRecord[],
	timestamp: number
}

interface BrowserDB extends DBSchema {
	files: {
		key: string,
		value: FileRecord[]
	}
}

export interface WriteOptions {
	writeRemote?: boolean,
	useDebouncer?: boolean
}


class FileSystem {
	private cache: Cache;
	private browserStorage: BrowserStorage;
	private remoteStorage: RemoteStorage;

	private MAX_FILE_RECORD = 3;
	private DEFAULT_WRITE_OPTIONS: WriteOptions = {
		useDebouncer: true,
		writeRemote: true
	}
	public utils: FSUtils;
	public events: FSEvents;

	public isInited: boolean;


	constructor(editorState: typeof useEditorState) {
		this.cache = new Cache(this);
		this.browserStorage = new BrowserStorage(this);
		this.remoteStorage = new RemoteStorage(this);
		this.utils = new FSUtils(this, editorState)
		this.events = new FSEvents(this)
		window.fs = {
			write: this.write,
			read: this.read.bind(this),
			readVersions: this.readVersions.bind(this),
			list: this.list.bind(this),
			delete: this.delete.bind(this)
		}
		this.isInited = false;
	}

	public async init() {
		await this.browserStorage.init();
		await this.remoteStorage.init();
		this.isInited = true;
		await this.events.emit("INIT");
	}

	write = async (path: string, content: string, options: WriteOptions = this.DEFAULT_WRITE_OPTIONS) => {
		try {
			options = { ...this.DEFAULT_WRITE_OPTIONS, ...options }
			let fileRecordList: FileRecord[] = [];
			path = this.utils.sanitizePath(path);

			if (this.cache.has(path)) {
				fileRecordList = this.cache.readAllVersion(path);

			} else if (await this.browserStorage.has(path)) {
				fileRecordList = await this.browserStorage.readAllVersion(path);

			}

			const latestRecord = this.utils.getLatestFileRecord(fileRecordList);
			if (latestRecord && latestRecord.content === content) {
				//Unnecessary write. Return early.
				return
			}

			const version = latestRecord ? latestRecord.metadata.version + 1 : 1;
			fileRecordList.push({
				content: content,
				metadata: {
					version: version,
					timestamp: Date.now()
				}
			})
			fileRecordList.sort((a, b) => a.metadata.version - b.metadata.version)
			if (fileRecordList.length > this.MAX_FILE_RECORD) {
				fileRecordList.shift()
			}


			this.cache.write(path, fileRecordList, options);
			//Note: You may assume execution waits until these writes completes but its not always the case because of the debouncers.
			// If debouncers is used then we won't wait until writes complete.
			await this.browserStorage.write(path, fileRecordList, options)
			if (options.writeRemote) {
				await this.remoteStorage.write(path, fileRecordList, options)
			}
			this.events.emit("WRITE", path, content);

		} catch (e) {
			message.error({ content: i18n.t("projectResources.writeError"), duration: 5, key: "projectResources-writeError" });
			throw new FileSystemError("Failed to write file", e as Error);
		}
	}

	public async read(path: string) {
		path = this.utils.sanitizePath(path);
		if (this.cache.has(path)) {
			return this.cache.read(path)!.content;
		}

		const isBrowserStorageHas = await this.browserStorage.read(path);
		if (isBrowserStorageHas) {
			return (await this.browserStorage.read(path))!.content;
		}

		return null;
	}

	public async readVersions(path: string) {
		path = this.utils.sanitizePath(path);
		if (this.cache.has(path)) {
			return this.cache.readAllVersion(path)
		}

		const isBrowserStorageHas = await this.browserStorage.read(path);
		if (isBrowserStorageHas) {
			return await this.browserStorage.readAllVersion(path);
		}

		return null;

	}

	public async list(path: string = "/") {
		path = this.utils.sanitizePath(path);
		const browserStorageKeys = await this.browserStorage.list(path);
		const cacheKeys = this.cache.list(path);
		const uniqueKeys = new Set([...browserStorageKeys, ...cacheKeys])
		return [...uniqueKeys];
	}

	public async delete(path: string) {
		path = this.utils.sanitizePath(path);

		this.cache.delete(path);
		//Cancel any pending write.
		this.remoteStorage.pendingWrites.delete(path);

		const allKeys = (await this.list(path)).map((relativePath) => {
			return this.utils.joinPath(path, relativePath);
		});

		await this.browserStorage.delete(allKeys);
		await this.remoteStorage.delete(path, allKeys);

	}

	public _getBrowserStorage() {
		return this.browserStorage;
	}

	public _getCache() {
		return this.cache;
	}

	public _getRemoteStorage() {
		return this.remoteStorage;
	}




}


class Cache {
	private cache: Map<string, CacheItem>
	private fileSystem: FileSystem;

	private CACHE_SIZE = 5;

	constructor(fileSystem: FileSystem) {
		this.fileSystem = fileSystem;
		this.cache = new Map();
	}

	public write(path: string, fileRecordList: readonly FileRecord[], options: WriteOptions) {
		const cacheItem = {
			fileRecordList: [...fileRecordList],
			timestamp: Date.now(),
		}
		this.cache.set(path, cacheItem);
	}

	public update() {
		// Cache is full delete the least recent accessed cache item.
		if (this.cache.size > this.CACHE_SIZE) {
			let leastRecentCacheItem = null;
			let leastRecentTimestamp = Number.POSITIVE_INFINITY;
			for (const [path, cacheItem] of this.cache.entries()) {
				if (cacheItem.timestamp < leastRecentTimestamp) {
					leastRecentCacheItem = path;
					leastRecentTimestamp = cacheItem.timestamp;
				}
			}
			if (leastRecentCacheItem) {
				this.cache.delete(leastRecentCacheItem);
			}
		}
	}

	public read(path: string) {
		const fileRecordList = this.readAllVersion(path);
		return this.fileSystem.utils.getLatestFileRecord(fileRecordList);
	}

	public readAllVersion(path: string) {
		const cacheItem = this.cache.get(path);

		if (cacheItem) {
			cacheItem.timestamp = Date.now();
			this.cache.set(path, cacheItem);
		}

		return cacheItem?.fileRecordList || []
	}


	public list(path = "/") {
		const allFileNames = Array.from(this.cache.keys());
		return allFileNames
			.filter((filePath) => filePath.startsWith(path))
			.map((filePath) => {
				filePath = filePath.replace(path, "").replace(/^\//, "");
				return filePath;
			});
	}

	public has(path: string) {
		return this.cache.has(path);
	}

	public clear() {
		this.cache.clear();
	}

	public delete(path: string) {
		if (this.cache.has(path)) {
			this.cache.delete(path);
		}
	}



}

class BrowserStorage {
	public fileSystem: FileSystem;
	public db: IDBPDatabase<BrowserDB> | undefined;
	public debouncers: Map<string, (path: string, record: readonly FileRecord[], options: WriteOptions) => void>;

	public WRITE_DEBOUNCE_TIME = 2000;
	private MAX_STACKED_DBS = 5;


	constructor(fileSystem: FileSystem) {
		this.fileSystem = fileSystem;
		this.debouncers = new Map();
	}

	public async init() {
		const appState = this.fileSystem.utils.getAppState();
		let dbName = appState.metadata?.is_preview ? "RidersFileSystem-Preview" : "RidersFileSystem";
		if (isPublicProject()) {
			dbName += "-Public";
		}

		const dbPrefix = dbName;
		if (appState.environment !== "local") {
			dbName += "-" + Date.now();
		}


		this.db = await openDB<BrowserDB>(dbName, undefined, {
			upgrade(db) {
				db.createObjectStore("files");
			}
		})

		this.cleanupOldDatabases(dbPrefix, dbName);
	}


	private async cleanupOldDatabases(prefix: string, currentDbName: string) {
		try {
			if (!indexedDB.databases) return;
			const allDatabases = await indexedDB.databases();
			const matchingDbs = allDatabases
				.filter(db => {
					if (!db.name || db.name === currentDbName) return false;
					if (db.name === prefix) return true; // match legacy exact name

					// Match timestamped names exactly for this prefix: prefix + "-" + digits
					// We construct a simple regex dynamically: ^Prefix-\d+$
					// Escape any special characters in prefix just in case (though we know it's alphanumeric/dash)
					const regex = new RegExp(`^${prefix}-\\d+$`);
					return regex.test(db.name);
				})
				.sort((a, b) => {
					// Extract timestamp from the end of the db name
					const tsA = parseInt(a.name!.split("-").pop() || "0");
					const tsB = parseInt(b.name!.split("-").pop() || "0");
					return tsB - tsA; // newest first
				});

			// Keep only MAX_STACKED_DBS - 1 (plus current = MAX_STACKED_DBS total)
			const toDelete = matchingDbs.slice(this.MAX_STACKED_DBS - 1);
			for (const db of toDelete) {
				if (db.name) {
					deleteDB(db.name).catch(() => {
						// Blocked by another tab — skip
					});
				}
			}
		} catch {
			// indexedDB.databases() not supported — skip cleanup
		}
	}

	public async read(path: string) {
		const fileRecordList = await this.readAllVersion(path);
		return this.fileSystem.utils.getLatestFileRecord(fileRecordList);
	}

	public async readAllVersion(path: string) {
		if (!this.db) {
			throw new Error("Browser storage not initialized");
		}

		const tx = await this.db.transaction("files", "readonly");
		const store = tx.objectStore("files");
		const fileRecordList = await store.get(path);

		await tx.done;
		return fileRecordList || [];
	}

	public async has(path: string) {
		if (!this.db) {
			throw new Error("Browser storage not initialized");
		}

		const tx = await this.db.transaction("files", "readonly");
		const store = tx.objectStore("files");
		const count = await store.count(path);
		await tx.done;
		return !!count;
	}

	public async write(path: string, fileRecordList: readonly FileRecord[], options: WriteOptions) {
		if (options.useDebouncer) {
			const writeDebouncer = this.getWriteDebouncer(path);
			await writeDebouncer(path, fileRecordList, options);
		} else {
			await this._write(path, fileRecordList, options);
		}
	}

	_write = async (path: string, fileRecordList: readonly FileRecord[], options: WriteOptions) => {
		if (!this.db) {
			throw new Error("Browser storage not initialized");
		}

		if (!this.fileSystem._getCache().has(path)) {
			// File deleted before this function call. Don't write it.
			return;
		}
		const tx = await this.db.transaction("files", "readwrite");
		const store = tx.objectStore("files");
		await store.put([...fileRecordList], path);
		await tx.done;
		this.fileSystem.events.emit("BROWSER_WRITE", path);
	}

	public async list(path: string = "/") {
		if (!this.db) {
			throw new Error("Browser storage not initialized");
		}

		const tx = await this.db.transaction("files", "readonly");
		const store = tx.objectStore("files");
		const allFileNames = await store.getAllKeys();
		return allFileNames
			.filter((filePath) => filePath.startsWith(path))
			.map((filePath) => {
				filePath = filePath.replace(path, "").replace(/^\//, "");
				return filePath;
			});
	}

	public async delete(paths: string[]) {
		if (!this.db) {
			throw new Error("Browser storage not initialized");
		}

		const tx = await this.db.transaction("files", "readwrite");
		const store = tx.objectStore("files");
		for (const path of paths) {
			await store.delete(path)
		}
		await tx.done;

	}

	private getWriteDebouncer(path: string) {
		if (this.debouncers.has(path)) {
			return this.debouncers.get(path)!;
		}

		const debouncer = debounce(this._write, this.WRITE_DEBOUNCE_TIME, {
			leading: false,
			trailing: true
		});

		this.debouncers.set(path, debouncer);
		return debouncer;

	}
}


class RemoteStorage {
	public fileSystem: FileSystem
	public gitStorage?: GitStorage
	public s3Storage?: S3Storage
	public ignores: string[];
	public debouncers: Map<string, (path: string, record: readonly FileRecord[], options: WriteOptions) => Promise<void> | undefined>;
	public pendingWrites: Set<string>;

	public WRITE_DEBOUNCE_TIME: number = 3000; //(ms);

	constructor(fileSystem: FileSystem) {
		this.fileSystem = fileSystem;
		this.ignores = [".blockly", ".editor", ".gitkeep", ".commit_id", ".build", ".git", "download_include"];
		this.debouncers = new Map();
		this.pendingWrites = new Set();
		this.WRITE_DEBOUNCE_TIME = this.fileSystem._getBrowserStorage().WRITE_DEBOUNCE_TIME + this.WRITE_DEBOUNCE_TIME;
	}

	public async init() {
		const appState = this.fileSystem.utils.getAppState();
		if (appState.metadata?.storage === "s3") {
			this.s3Storage = new S3Storage(this);
			await this.s3Storage.init();
		} else if (appState.metadata?.storage === "none") {
			// Do nothing
		} else {
			this.gitStorage = new GitStorage(this);
			await this.gitStorage.init();
		}
	}

	public async write(path: string, fileRecordList: FileRecord[], options: WriteOptions) {
		const isIgnore = this.ignores.some((file) => path.includes(file))
		if (isIgnore) {
			return;
		}

		this.pendingWrites.add(path);
		this.fileSystem.utils.handleLeaveListener();

		if (options.useDebouncer) {
			const writeDebouncer = this.getWriteDebouncer(path);
			await writeDebouncer(path, fileRecordList, options);
		} else {
			await this._write(path, fileRecordList, options);
		}
	}
	_write = async (path: string, fileRecordList: readonly FileRecord[], options: WriteOptions) => {
		try {
			if (!this.pendingWrites.has(path)) {
				//File is deleted by the time this function is called.
				return;
			}

			if (this.s3Storage) {
				await this.s3Storage.write(path, fileRecordList, options)
			} else if (this.gitStorage) {
				await this.gitStorage.write(path, fileRecordList, options);
			}

		} catch (e) {
			message.error({ content: i18n.t("projectResources.writeError"), duration: 5, key: "projectResources-writeError" });
			throw new FileSystemError("Failed to write file", e as Error);
		} finally {

			this.pendingWrites.delete(path);
			this.fileSystem.events.emit("REMOTE_WRITE", path);

		}
	}

	public async delete(path: string, files: string[] = []) {
		if (this.s3Storage) {
			await this.s3Storage.delete(files)
		} else if (this.gitStorage) {
			await this.gitStorage.delete(path);
		}
	}

	private getWriteDebouncer(path: string) {
		if (this.debouncers.has(path)) {
			return this.debouncers.get(path)!;
		}

		const debouncer = debounce(this._write, this.WRITE_DEBOUNCE_TIME, {
			leading: false,
			trailing: true
		});

		this.debouncers.set(path, debouncer);
		return debouncer;

	}

	public getPendingWrites() {
		return [...this.pendingWrites];
	}

}

class GitStorage {
	public remoteStorage: RemoteStorage;
	public lfs: LFS.PromisifiedFS | undefined;
	public rootPath: string;

	constructor(remoteStorage: RemoteStorage) {
		this.remoteStorage = remoteStorage;
		this.rootPath = "/";
	}

	public async init() {
		const appState = this.remoteStorage.fileSystem.utils.getAppState();
		const dbName = appState.metadata?.is_preview ? "LFS-Preview" : "LFS"

		this.lfs = new LFS(dbName, { wipe: true }).promises;

		await this.createDir(this.rootPath);

		if (appState.metadata?.storage === "git") {
			//There is an existing git repo in the server.
			await this.git_clone(appState.metadata?.git_address, appState.metadata?.git_branch_name);
			const files = await this.readFilesRecursive("/");
			for (const [fileName, fileContent] of Object.entries(files)) {
				await this.remoteStorage.fileSystem.write(fileName, fileContent, { writeRemote: false, useDebouncer: false });
			}

		} else {
			// User opens first time.
			await this.git_init();
		}
	}

	public async write(path: string, fileRecordList: readonly FileRecord[], options: WriteOptions) {
		if (!this.lfs) {
			throw new Error("LFS not initialized");
		}

		const fileRecord = this.remoteStorage.fileSystem.utils.getLatestFileRecord(fileRecordList);
		if (!fileRecord) {
			return;
		}

		await this.createDir(path);
		await this.lfs.writeFile(path, fileRecord.content, "utf8");
		await git.add({ fs: this.lfs, dir: this.rootPath, filepath: this.relativePath(path) });
		await git.commit({
			fs: this.lfs, dir: this.rootPath, message: "Riders auto commit", author: {
				name: "riders",
				email: "riders@riders.ai",
			}
		});
		await git.push({
			fs: this.lfs,
			http,
			dir: this.rootPath,
			remote: "origin",
			force: true,
		});
	}

	public async delete(path: string) {
		if (!this.lfs) {
			throw new Error("LFS not initialized");
		}

		try {
			const stat = await this.lfs.stat(path).catch(() => null);

			if (!stat) {
				return 0;
			}

			if (stat.isDirectory()) {
				await this.lfs.rmdir(path);
				return;
			} else {
				await this.lfs.unlink(path);
				await git.remove({
					fs: this.lfs,
					dir: this.rootPath,
					filepath: path,
				});
				return 1;
			}
		} catch (error: any) {
			Sentry.captureException(new FileSystemError("Failed to delete file", error));
			return 0;
		}
	}

	public async createDir(path: string) {
		if (!this.lfs) {
			throw new Error("LFS not initialized");
		}

		const pathList = path.split("/");
		let parentPath = ""

		for (const dirName of pathList) {
			try {
				parentPath = this.joinPath(parentPath, dirName);
				const exists = await this.lfs.stat(parentPath);
			} catch (e) {
				await this.lfs.mkdir(parentPath);

			}
		}
	}

	public async git_init() {
		if (!this.lfs) {
			throw new Error("LFS not initialized");
		}

		await git.init({
			fs: this.lfs,
			dir: this.rootPath,
		});
	}

	public async git_clone(url: string, branch: string) {
		if (!this.lfs) {
			throw new Error("LFS not initialized");
		}

		await git.clone({
			fs: this.lfs,
			http: http,
			dir: this.rootPath,
			//corsProxy: (activeProxyServerAddress as string) || activeProxyServer,
			url,
			singleBranch: true,
			ref: branch,
			depth: 1,
			//cache: { type: "immutable" },
		});
		console.log("git: clone completed");
		const files = await this.lfs.readdir(this.rootPath);
		console.log("git clone files", files);
	}

	public async readFilesRecursive(path: string, files: { [key: string]: string } = {}): Promise<{ [keys: string]: string }> {
		if (!this.lfs) {
			throw new Error("LFS not initialized");
		}

		const isIgnore = this.remoteStorage.ignores.some((file) => path.includes(file))
		if (isIgnore) {
			return files;
		}

		try {
			const stats = await this.lfs.stat(path);
			if (stats.isDirectory()) {
				const dirContent = await this.lfs.readdir(path);
				for (const name of dirContent) {
					const contents = await this.readFilesRecursive(this.joinPath(path, name), { ...files });
					files = { ...files, ...contents };
				}
			} else if (stats.isFile()) {
				const fileContent = await this.lfs.readFile(path, "utf8")
				files[path] = fileContent
			}
		} catch (e) {
			return files;
		}

		return files;
	}

	public joinPath(...parts: string[]): string {
		return parts
			.join("/")
			.replace(/\/+/g, "/"); // collapse multiple slashes
	}

	public relativePath(path: string): string {
		let relative = path.startsWith(this.rootPath)
			? path.slice(this.rootPath.length)
			: path;
		// Remove leading slash so it's truly relative
		relative = relative.replace(/^\/+/, "");
		return relative || ".";
	}


}

// editor-api (project resources). The US build points at editor-api-us via VITE_EDITOR_API_URL.
const EDITOR_API_URL =
	import.meta.env.VITE_EDITOR_API_URL || "https://uztf5p7l5g.execute-api.eu-west-1.amazonaws.com";

class S3Storage {
	public remoteStorage: RemoteStorage;


	constructor(remoteStorage: RemoteStorage) {
		this.remoteStorage = remoteStorage;
	}

	public async init() {
		const files = await this.getFiles();
		for (const [fileName, content] of Object.entries(files)) {
			await this.remoteStorage.fileSystem.write(fileName, content as string, { writeRemote: false, useDebouncer: false });
		}
	}

	public async getFiles(nextToken: string | null = null, files: Record<string, string> = {}): Promise<Record<string, string>> {
		try {
			const appState = this.remoteStorage.fileSystem.utils.getAppState()
			const accessToken = appState.tokens?.express_api_token;
			const { data } = await axios.get(
				`${EDITOR_API_URL}/api/project-resources${nextToken ? `?continuationToken=${nextToken}` : ""
				}`,
				{
					headers: {
						Authorization: `Bearer ${accessToken}`,
					},
				}
			);
			if (data.nextToken) {
				return await this.getFiles(
					data.nextToken,
					{ ...files, ...data.files }
				);
			} else {
				return { ...files, ...data.files }

			}
		} catch (error) {
			Sentry.captureException(new FileSystemError("Failed to list files", error as Error));
			return {};
		}
	}

	public async write(path: string, fileRecordList: readonly FileRecord[], options: WriteOptions) {
		const fileContent = fileRecordList[fileRecordList.length - 1].content;

		// parital form data ile dosya yükleme
		const formData = new FormData();
		// file format file:
		const blob = new Blob([fileContent], {
			type: "application/octet-stream",
		});
		formData.append("file", blob, encodeURIComponent(path));

		const appState = this.remoteStorage.fileSystem.utils.getAppState()
		const accessToken = appState.tokens?.express_api_token;
		await axios.post(`${EDITOR_API_URL}/api/project-resources`, formData, {
			headers: {
				Authorization: `Bearer ${accessToken}`,
			},
		})

	}

	public async delete(files: string[]) {
		try {
			const appState = this.remoteStorage.fileSystem.utils.getAppState()
			const accessToken = appState.tokens?.express_api_token;
			await axios.delete(`${EDITOR_API_URL}/api/project-resources`, {
				data: { files },
				headers: {
					Authorization: `Bearer ${accessToken}`,
				},
			})
			return 1;

		} catch (e) {
			Sentry.captureException(new FileSystemError("Failed to delete file", e as Error));
			return 0;
		}
	}


}


/**
 * Upper bound on how long leaveListener waits for pending uploads to land.
 * Generous, because the alternative to waiting is losing a student's work, but
 * bounded, because a dead connection must not leave the window unclosable.
 */
const LEAVE_FLUSH_TIMEOUT_MS = 12000;

class FSUtils {
	private appState: typeof useEditorState;
	public fileSystem: FileSystem;

	private isLeaveListenerDisabled = false;

	constructor(fs: FileSystem, appState: typeof useEditorState) {
		this.fileSystem = fs;
		this.appState = appState
	}

	public getAppState() {
		return this.appState.getState();
	}

	public getLatestFileRecord(fileRecordList: readonly FileRecord[]) {
		let latestElement: FileRecord | null = null;
		let latestVersionNumber = -1;
		for (const entry of fileRecordList) {
			if (entry.metadata.version > latestVersionNumber) {
				latestElement = entry;
				latestVersionNumber = entry.metadata.version;
			}
		}
		return latestElement;
	}

	public sanitizePath(path: string) {
		// Path should always start with "/" and there shouldn't be trailing "/" in the end 
		// example: /usercode/scenario1.py

		if (!path || path === "/") {
			return "/";
		}

		path = path.trim()
		path = "/" + path;
		return path
			.replace(/\/+/g, "/")   // collapse multiple slashes
			.replace(/\/$/, "");     // remove trailing slash

	}

	public joinPath(...parts: string[]): string {
		return parts
			.join("/")
			.replace(/\/+/g, "/") // collapse multiple slashes
			.replace(/\/$/, "");     // remove trailing slash
	}

	public getPendingRemoteWrites() {
		return this.fileSystem._getRemoteStorage().getPendingWrites();
	}

	leaveListener = async (event?: any) => {
		if (event) {
			event.preventDefault();
			event.returnValue = "Değişikliklerinizi kaydettiniz mi?";
		}

		message.open({
			key: "leave",
			type: "loading",
			content: i18n.t("savingMessage"),
			duration: 0,
			className: "messagebox",
		});

		await new Promise<null>((resolve) => {
			const isDone = () => this.getPendingRemoteWrites().length === 0;

			// Check before polling: with nothing pending there is no reason to
			// sit through the first 500ms tick.
			if (isDone()) {
				resolve(null);
				return;
			}

			// The interval used to be left running forever -- harmless on the
			// web, where the page was unloading anyway, but the desktop close
			// guard calls this in a window that may well stay open, so it has
			// to clean up after itself.
			const interval = setInterval(() => {
				if (!isDone()) return;
				clearInterval(interval);
				clearTimeout(timeout);
				resolve(null);
			}, 500);

			// And it must not wait forever on a connection that is not coming
			// back; the caller decides what to do with a partial flush.
			const timeout = setTimeout(() => {
				clearInterval(interval);
				resolve(null);
			}, LEAVE_FLUSH_TIMEOUT_MS);
		})

		message.open({
			key: "leave",
			type: "success",
			content: i18n.t("savedMessage"),
			duration: 3,
			className: "messagebox",
		});


		return event?.returnValue || "";
	}

	handleLeaveListener = () => {
		const remotePendingWrites = this.getPendingRemoteWrites();
		if (remotePendingWrites.length > 0 && !this.isLeaveListenerDisabled) {
			window.addEventListener("beforeunload", this.fileSystem.utils.leaveListener);
		} else {
			window.removeEventListener("beforeunload", this.fileSystem.utils.leaveListener);
		}
	}

	public disableLeaveListener() {
		this.isLeaveListenerDisabled = true;
		window.removeEventListener("beforeunload", this.fileSystem.utils.leaveListener);
	}


}

type FSEvent = "INIT" | "WRITE" | "BROWSER_WRITE" | "REMOTE_WRITE";
class FSEvents {

	private fileSystem: FileSystem;
	private eventListeners: Map<FSEvent, Map<string, (...arg: any[]) => Promise<void>>>;
	private listenerIdIndex;


	constructor(fs: FileSystem) {
		this.fileSystem = fs;
		this.eventListeners = new Map();
		this.listenerIdIndex = 1;
	}

	public registerEventListener(event: FSEvent, fn: (...arg: any[]) => Promise<void>) {
		const listenersMap = this.eventListeners.get(event) || new Map();
		listenersMap.set(this.listenerIdIndex, fn);
		const listenerId = this.listenerIdIndex;
		this.listenerIdIndex++;
		this.eventListeners.set(event, listenersMap);

		return listenerId;
	}

	public unregisterEventListener(event: FSEvent, index: number) {
		const listenersMap = this.eventListeners.get(event) || new Map();
		listenersMap.delete(index);
	}

	public async emit(event: FSEvent, ...arg: any[]) {
		const listenersMap = this.eventListeners.get(event) || new Map();

		for (const fn of listenersMap.values()) {
			await fn(...arg);
		}
	}

	public getEventListeners() {
		return this.eventListeners;
	}
}

export const FS = new FileSystem(useEditorState);

FS.events.registerEventListener("REMOTE_WRITE", async (path: string) => {
	FS.utils.handleLeaveListener();

	const app_state = FS.utils.getAppState();
	if (path === app_state.pythonCodePath) {
		clearMonacoTabName();
	}
	if (path === app_state.blocklyCodePath) {
		clearBlocklyTabName();
	}
});

FS.events.registerEventListener("BROWSER_WRITE", async (path: string) => {
	const cache = FS._getCache();
	cache.update();

});

FS.events.registerEventListener("WRITE", async (path: string, content: string) => {
	const app_state = FS.utils.getAppState();
	if (path === app_state.pythonCodePath) {
		updateMonacoTabName();
	}
	if (path === app_state.blocklyCodePath) {
		updateBlocklyTabName();
	}
});

FS.events.registerEventListener("INIT", async () => {
	try {
		const blocklyStoreData = await FS.read("store/blocklyStore.json");
		useEditorState.getState().initBlocklyVariables(JSON.parse(blocklyStoreData || "{}"));
	} catch (e) {
		useEditorState.getState().initBlocklyVariables();
	} finally {
		useEditorState.getState().subscribeBlocklyVariables((state) => {
			FS.write(
				"store/blocklyStore.json",
				JSON.stringify(state)
			).then(() => { });
		});
	}

	try {
		const savedEditorState = await FS.read(".states/editorState.json")
		useEditorState.getState().setPrevEditorState(savedEditorState);

	} catch (e) {
		// Do nothing
	}

	useEditorState.subscribe(debounce(async (state, prevState) => {
		if (useEditorState.getState().initialized && useEditorState.getState().saveStatus === "NEEDSAVE") {
			useEditorState.getState().setSaveStatus("SAVING");
			await FS.write(
				".states/editorState.json",
				useEditorState.getState().toJson(),
				{ useDebouncer: false }
			);
			useEditorState.getState().setSaveStatus("SAVED");
		}
	}, 1000));

});



